import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useFormik } from 'formik'
import {
  vitalsValidationSchema as validationRules,
  vitalsMessages,
  vitalsComponent,
  visitDashboardConstants,
  type BloodGlucoseType,
  type TemperatureUnit,
  type Posture,
  getBMIStatus,
} from '../constants/Constants'
import {
  createCampPatientVitals,
  getCampPatientVitals,
  updateCampPatientVitals,
} from '../../../services/CampManagementService'
import * as Yup from 'yup'
import { styled } from '@mui/material/styles'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import styles from './CampPatientVitals.module.css'
import Height from '../../opdPatientView/Height'
import SleepAnalysis, {
  type SleepAnalysisHandle,
  type StopBangFormValues,
} from '../../opdPatientView/SleepAnalysis'

interface VitalsFormValues {
  heartRate: string
  respiratoryRate: string
  systolicBp: string
  diastolicBp: string
  spo2: string
  temperature: string
  bloodGlucose: string
  bloodGlucoseType: BloodGlucoseType
  height: string
  weight: string
  bmi: string
  temperatureUnit: TemperatureUnit
  posture: Posture
}

interface CampPatientVitalsComponentProps {
  intent?: 'view' | 'edit' | 'create'
  appointmentData?: {
    visitId?: string
    uhid?: string
    campId?: string
  }
  onSuccess?: () => void
  patientData?: any
  selectedVisit?: any
}

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2, 0),
}))

const StyledFormGrid = styled(Grid)(({ theme }) => ({
  '& > .MuiGrid-item': {
    padding: theme.spacing(1, 2),
  },
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: theme.spacing(1),
}))

const FieldContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiFormControl-root': {
    width: '100%',
  },
}))

const FieldLabel = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}))

const BmiBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}))

// No-operation function with explicit void return type
const noop = (): void => {
  // This is an intentional no-op function
}

const CampPatientVitals: React.FC<CampPatientVitalsComponentProps> = ({
  intent = vitalsMessages.intents.create,
  onSuccess = noop,
  appointmentData,
  patientData,
  selectedVisit,
}) => {
  const [vitalsId, setVitalsId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [sleepData, setSleepData] = useState<StopBangFormValues | null>(null)
  const sleepAnalysisRef = useRef<SleepAnalysisHandle>(null)

  // Form validation schema
  const validationSchema = Yup?.object?.({
    heartRate: Yup?.number?.()
      ?.typeError?.(validationRules?.heartRate?.typeError)
      ?.nullable?.(),
    respiratoryRate: Yup?.number?.()
      ?.typeError?.(validationRules?.respiratoryRate?.typeError)
      ?.nullable?.(),
    systolicBp: Yup?.number?.()
      ?.typeError?.(validationRules?.systolicBp?.typeError)
      ?.nullable?.(),
    diastolicBp: Yup?.number?.()
      ?.typeError?.(validationRules?.diastolicBp?.typeError)
      ?.nullable?.(),
    spo2: Yup?.number?.()
      ?.typeError?.(validationRules?.spo2?.typeError)
      ?.nullable?.(),
    temperature: Yup?.number?.()
      ?.typeError?.(validationRules?.temperature?.typeError)
      ?.nullable?.(),
    bloodGlucose: Yup?.number?.()
      ?.typeError?.(validationRules?.bloodGlucose?.typeError)
      ?.nullable?.(),
    height: Yup?.number?.()
      ?.typeError?.(validationRules?.height?.typeError)
      ?.nullable?.(),
    weight: Yup?.number?.()
      ?.typeError?.(validationRules?.weight?.typeError)
      ?.nullable?.(),
    posture: Yup?.string()?.required?.(validationRules?.posture?.requiredError),
  })

  const prepareVitalsPayload = (
    values: Partial<VitalsFormValues>,
    stopBang?: StopBangFormValues
  ) => ({
    campId: appointmentData?.campId,
    patientUhid: appointmentData?.uhid,
    encounterId: appointmentData?.visitId,
    visitType: vitalsMessages.defaults.visitType,
    pulse: values?.heartRate ?? vitalsMessages.defaults.number,
    temperature: values?.temperature ?? vitalsMessages.defaults.number,
    respiratoryRate: values?.respiratoryRate ?? vitalsMessages.defaults.number,
    spo2: values?.spo2 ?? vitalsMessages.defaults.number,
    systolic: values?.systolicBp ?? vitalsMessages.defaults.number,
    diastolic: values?.diastolicBp ?? vitalsMessages.defaults.number,
    posture: values?.posture ?? vitalsMessages.defaults.defaultPosture,
    rbs:
      values?.bloodGlucoseType ===
      vitalsMessages.defaults.bloodGlucoseTypes.random
        ? values?.bloodGlucose ?? vitalsMessages.defaults.number
        : vitalsMessages.defaults.number,
    fbs:
      values?.bloodGlucoseType ===
      vitalsMessages.defaults.bloodGlucoseTypes.fasting
        ? values?.bloodGlucose ?? vitalsMessages.defaults.number
        : vitalsMessages.defaults.number,
    ppbs:
      values?.bloodGlucoseType ===
      vitalsMessages.defaults.bloodGlucoseTypes.postprandial
        ? values?.bloodGlucose ?? vitalsMessages.defaults.number
        : vitalsMessages.defaults.number,
    height: values?.height ?? vitalsMessages.defaults.number,
    weight: values?.weight ?? vitalsMessages.defaults.number,
    bmi: values?.bmi ?? vitalsMessages.defaults.number,
    sbq: stopBang,
  })

  const handleSubmit = async (
    values: Partial<VitalsFormValues>,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      setIsLoading(true)

      // Validate and collect STOP-Bang questionnaire values (only for General Medicine)
      const isGeneralMedicine =
        selectedVisit?.speciality?.toLowerCase() === 'general medicine'
      let stopBangValues: StopBangFormValues | undefined
      if (isGeneralMedicine) {
        const isSleepValid = await sleepAnalysisRef?.current?.validate()
        if (!isSleepValid) {
          toast.error('Please complete the Sleep Analysis questionnaire.')
          setIsLoading(false)
          setSubmitting(false)
          return
        }
        stopBangValues = sleepAnalysisRef.current?.getValues()
      }

      // Transform form values to match API payload
      const payload = prepareVitalsPayload(values, stopBangValues)

      let response
      if (vitalsId) {
        try {
          const existingData = await getCampPatientVitals(
            appointmentData?.visitId ?? ''
          )
          const fhirRecordId = existingData?.data?.fhirRecordId ?? vitalsId
          const updatePayload = {
            ...payload,
            id: vitalsId,
            fhirRecordId,
          }

          response = await updateCampPatientVitals(updatePayload)
        } catch (error) {
          console.error('Error updating vitals:', error)
          throw error
        }
      } else {
        response = await createCampPatientVitals(payload)
        if (response?.success && response?.data?.id) {
          setVitalsId(response?.data?.id)
        }
      }

      if (response?.success) {
        toast.success(
          vitalsId
            ? vitalsMessages.success.updated
            : vitalsMessages.success.saved
        )
        onSuccess()
      } else {
        toast.error(
          response?.message ??
            (vitalsId
              ? vitalsMessages.errors.updateFailed
              : vitalsMessages.errors.saveFailed)
        )
      }
    } catch (error: any) {
      toast.error(error?.message ?? vitalsMessages.errors.saveError)
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  // Formik form
  const formik = useFormik<Partial<VitalsFormValues>>({
    initialValues: {
      heartRate: vitalsComponent.defaults.emptyString,
      respiratoryRate: vitalsComponent.defaults.emptyString,
      systolicBp: vitalsComponent.defaults.emptyString,
      diastolicBp: vitalsComponent.defaults.emptyString,
      spo2: vitalsComponent.defaults.emptyString,
      temperature: vitalsComponent.defaults.emptyString,
      bloodGlucose: vitalsComponent.defaults.emptyString,
      bloodGlucoseType: vitalsComponent.defaults
        .defaultBloodGlucoseType as BloodGlucoseType,
      height: vitalsComponent.defaults.emptyString,
      weight: vitalsComponent.defaults.emptyString,
      bmi: vitalsComponent.defaults.emptyString,
      temperatureUnit: vitalsComponent.defaults
        .defaultTemperatureUnit as TemperatureUnit,
      posture: vitalsComponent.defaults.defaultPosture as Posture,
    },
    validationSchema,
    onSubmit: handleSubmit,
  })

  const formikRef = useRef(formik)
  formikRef.current = formik

  const fetchVitals = useCallback(async () => {
    if (!appointmentData?.visitId) return

    try {
      const response = await getCampPatientVitals(appointmentData?.visitId)
      if (response?.success) {
        // Store the vitals ID if it exists
        if (response?.data?.id) {
          setVitalsId(response?.data?.id)
        }
        // Determine blood glucose type based on available values
        let bloodGlucoseType: BloodGlucoseType = vitalsMessages?.defaults
          ?.defaultBloodGlucoseType as BloodGlucoseType
        let bloodGlucoses: any

        if (Number(response?.data?.rbs) > 0) {
          bloodGlucoses = response?.data?.rbs
          bloodGlucoseType = vitalsMessages?.defaults?.bloodGlucoseTypes
            ?.random as BloodGlucoseType
        }
        if (Number(response?.data?.fbs) > 0) {
          bloodGlucoses = response?.data?.fbs
          bloodGlucoseType = vitalsMessages?.defaults?.bloodGlucoseTypes
            ?.fasting as BloodGlucoseType
        } else if (Number(response?.data?.ppbs) > 0) {
          bloodGlucoses = response?.data?.ppbs
          bloodGlucoseType = vitalsMessages?.defaults?.bloodGlucoseTypes
            ?.postprandial as BloodGlucoseType
        }

        formikRef?.current?.setValues(
          {
            heartRate:
              response?.data?.pulse ?? vitalsMessages.defaults.emptyString,
            respiratoryRate:
              response?.data?.respiratoryRate ??
              vitalsMessages.defaults.emptyString,
            systolicBp:
              response?.data?.systolic ?? vitalsMessages.defaults.emptyString,
            diastolicBp:
              response?.data?.diastolic ?? vitalsMessages.defaults.emptyString,
            spo2: response?.data?.spo2 ?? vitalsMessages.defaults.emptyString,
            temperature:
              response?.data?.temperature ??
              vitalsMessages.defaults.emptyString,
            bloodGlucose: bloodGlucoses || vitalsMessages.defaults.emptyString,
            bloodGlucoseType,
            height:
              response?.data?.height ?? vitalsMessages.defaults.emptyString,
            weight:
              response?.data?.weight ?? vitalsMessages.defaults.emptyString,
            bmi: response?.data?.bmi ?? vitalsMessages.defaults.emptyString,
            posture:
              response?.data?.posture ??
              (vitalsMessages.defaults.defaultPosture as Posture),
          },
          false
        )
        setSleepData(response?.data?.sbq ?? null)
      } else {
        toast.error(response?.message ?? vitalsMessages.errors.fetchFailed)
      }
    } catch (error: any) {
      toast.error(error?.message ?? vitalsMessages.errors.fetchFailed)
    }
  }, [appointmentData?.visitId]) // Only recreate if visitId changes

  useEffect(() => {
    fetchVitals()
  }, [appointmentData?.visitId, formikRef, fetchVitals])

  // Always allow editing for now
  const isFieldDisabled = () => false

  // Render form field
  const renderFormField = (
    name: keyof VitalsFormValues,
    label: string,
    type = 'text',
    options?: Array<{ value: string; label: string }>,
    unit?: string,
    showUnitToggle = false
  ) => {
    const value = formik?.values?.[name] ?? ''
    const error = formik?.touched?.[name] && Boolean(formik?.errors?.[name])
    const helperText = formik?.touched?.[name] && formik?.errors?.[name]
    const disabled = isFieldDisabled()

    const handleSelectChange = (e: any, fieldName: keyof VitalsFormValues) => {
      if (
        intent === vitalsMessages?.intents?.view &&
        !formik?.values?.[fieldName]
      ) {
        // Allow changes in view mode only for empty fields
        formik?.handleChange(e)
      } else if (intent !== vitalsMessages?.intents?.view) {
        // Allow all changes in edit/create mode
        formik?.handleChange(e)
      } else {
        formik?.handleChange(e)
      }
    }

    const handleTextFieldChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldName: keyof VitalsFormValues
    ) => {
      const { value } = e.target
      // For number fields, only allow numbers and one decimal point
      if (type === visitDashboardConstants?.defaults?.number) {
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
          formik?.setFieldValue(fieldName, value)
        }
      } else {
        formik?.handleChange(e)
      }
    }

    const handleTextFieldBlur = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldName: keyof VitalsFormValues
    ) => {
      // Convert empty string to undefined for number fields
      if (
        type === visitDashboardConstants?.defaults?.number &&
        e?.target?.value === ''
      ) {
        formik?.setFieldValue(fieldName, undefined)
      }
      formik?.handleBlur(e)
    }

    return (
      <Grid item xs={12} sm={6} md={4} key={name}>
        <FieldContainer>
          <FieldLabel variant='caption'>
            {label} {unit && `(${unit})`}
          </FieldLabel>
          {type === vitalsMessages?.fieldTypes?.select ? (
            <FormControl fullWidth size='small' error={error}>
              <InputLabel id={`${name}-label`}>{label}</InputLabel>
              <Select
                labelId={`${name}-label`}
                id={name}
                name={name}
                value={value}
                onChange={(e) => handleSelectChange(e, name)}
                onBlur={formik?.handleBlur}
                label={label}
                disabled={disabled}
              >
                {options?.map((option) => (
                  <MenuItem key={option?.value} value={option?.value}>
                    {option?.label}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText>{helperText}</FormHelperText>}
            </FormControl>
          ) : (
            <Box className={styles.flexGap}>
              <TextField
                fullWidth
                size='small'
                type={type}
                name={name}
                value={value}
                onChange={(e) => handleTextFieldChange(e, name)}
                onBlur={(e) => handleTextFieldBlur(e, name)}
                error={error}
                disabled={disabled}
                helperText={helperText}
                inputProps={{
                  inputMode:
                    type === visitDashboardConstants?.defaults?.number
                      ? 'decimal'
                      : 'text',
                  pattern:
                    type === visitDashboardConstants?.defaults?.number
                      ? '\\d*\\.?\\d*'
                      : '.*',
                }}
                InputProps={{
                  endAdornment: showUnitToggle ? (
                    <Box className={styles.temperatureUnit}>
                      <Typography variant='body2'>°F</Typography>
                    </Box>
                  ) : null,
                }}
              />
            </Box>
          )}
        </FieldContainer>
      </Grid>
    )
  }

  // BMI Display Component
  const BmiDisplay = () => {
    const bmiContainerRef = useRef<HTMLDivElement>(null)
    const bmiStatus = getBMIStatus(Number(formik?.values?.bmi) ?? 0)

    useEffect(() => {
      if (bmiContainerRef?.current && bmiStatus) {
        bmiContainerRef?.current?.style?.setProperty(
          '--bmi-color',
          bmiStatus?.color
        )
      }
    }, [bmiStatus])

    return (
      <BmiBox>
        <div ref={bmiContainerRef} className={styles.bmiColorContainer}>
          <TextField
            fullWidth
            value={formik?.values?.bmi ?? ''}
            disabled
            className={styles.bmiInput}
          />
        </div>
        {bmiStatus !== null && bmiStatus !== undefined && (
          <Box display='flex' alignItems='center' gap={1}>
            <Typography variant='body2' color={bmiStatus?.color}>
              BMI: {formik?.values?.bmi} ({bmiStatus?.status})
            </Typography>
          </Box>
        )}
      </BmiBox>
    )
  }

  const { setFieldValue } = formik

  // Use the imported getBMIStatus function directly

  const calculateBMI = useCallback((height: number, weight: number): number => {
    if (!height || !weight) return 0
    // Convert height from cm to meters
    const heightInMeters = height / 100
    // Calculate BMI (weight in kg / (height in m)^2)
    return Number((weight / (heightInMeters * heightInMeters)).toFixed(1))
  }, [])

  useEffect(() => {
    const height = Number(formik?.values?.height)
    const weight = Number(formik?.values?.weight)
    const bmi = calculateBMI(height, weight)
    setFieldValue('bmi', bmi)
  }, [
    formik?.values?.height,
    formik?.values?.weight,
    calculateBMI,
    setFieldValue,
  ])

  return (
    <StyledPaper>
      <form onSubmit={formik?.handleSubmit}>
        <StyledFormGrid container spacing={2}>
          <Grid item xs={12}>
            <SectionTitle variant='h6'>Vitals</SectionTitle>
          </Grid>
          {/* Vitals Fields */}
          {vitalsComponent?.fields?.map((field) =>
            renderFormField(
              field.name as keyof VitalsFormValues,
              field.label,
              field.type,
              undefined,
              field.unit,
              field.showUnitToggle
            )
          )}

          {/* Blood Glucose */}
          <Grid item xs={12} sm={6} md={4}>
            <FieldContainer>
              <FieldLabel variant='caption'>Blood Glucose (mg/dL)</FieldLabel>
              <Box className={styles.flexGap}>
                <FormControl size='small'>
                  <InputLabel id='blood-glucose-type-label'>Type</InputLabel>
                  <Select
                    labelId='blood-glucose-type-label'
                    id='bloodGlucoseType'
                    name='bloodGlucoseType'
                    value={formik?.values?.bloodGlucoseType}
                    onChange={formik?.handleChange}
                    label='Type'
                  >
                    <MenuItem value='random'>Random</MenuItem>
                    <MenuItem value='fasting'>Fasting</MenuItem>
                    <MenuItem value='postprandial'>Postprandial</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size='small'
                  type='number'
                  name='bloodGlucose'
                  value={formik?.values?.bloodGlucose ?? ''}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  // disabled={intent === 'view'}
                  error={
                    formik?.touched?.bloodGlucose &&
                    Boolean(formik?.errors?.bloodGlucose)
                  }
                  helperText={
                    formik?.touched?.bloodGlucose &&
                    formik?.errors?.bloodGlucose
                      ? formik.errors.bloodGlucose
                      : ' ' // Show empty helper text to maintain layout
                  }
                  inputProps={{
                    step: '0.1',
                    min: '0',
                    inputMode: 'decimal',
                    pattern: '\\d*\\.?\\d*',
                  }}
                />
              </Box>
            </FieldContainer>
          </Grid>

          {/* Anthropometry Section */}
          <Grid item xs={12}>
            <SectionTitle variant='h6'>Anthropometry</SectionTitle>
          </Grid>

          {/* {renderFormField(
            'height',
            vitalsComponent?.labels?.height,
            'number',
            undefined,
            vitalsComponent?.units?.cm
          )} */}
          <Grid item xs={12} sm={8} md={8} lg={8} xl={4}>
            <FieldLabel variant='caption'>Height</FieldLabel>
            <Height
              value={Number(formik?.values?.height ?? '')}
              onChange={(value: any) => {
                formik?.setFieldValue('height', value)
              }}
              required={true}
              error={!!formik?.errors?.height && formik?.touched?.height}
              helperText={
                formik?.errors?.height && formik?.touched?.height
                  ? formik?.errors?.height
                  : ''
              }
              defaultDisplayMode='cm'
            />
          </Grid>

          {renderFormField(
            'weight',
            vitalsComponent?.labels?.weight,
            'number',
            undefined,
            vitalsComponent?.units?.kg
          )}

          {/* BMI Display */}
          <Grid item xs={12} sm={6} md={4}>
            <FieldContainer>
              <FieldLabel variant='caption'>BMI</FieldLabel>
              <BmiDisplay />
            </FieldContainer>
          </Grid>

          {/* Posture */}
          {(() => {
            const postures = vitalsComponent?.options?.postures ?? []
            return (
              vitalsComponent?.labels?.posture &&
              renderFormField(
                'posture',
                vitalsComponent?.labels?.posture,
                'select',
                [...postures]
              )
            )
          })()}

          {/* Sleep Analysis – STOP-Bang Questionnaire (only for General Medicine) */}
          {selectedVisit?.speciality?.toLowerCase() === 'general medicine' && (
            <Grid item xs={12}>
              <SectionTitle variant='h6'>Sleep Analysis</SectionTitle>
              <SleepAnalysis
                ref={sleepAnalysisRef}
                prefillData={sleepData ?? undefined}
                height={String(formik?.values?.height ?? '')}
                weight={String(formik?.values?.weight ?? '')}
                bmi={
                  formik?.values?.bmi && Number(formik?.values?.bmi) > 0
                    ? String(formik?.values?.bmi)
                    : ''
                }
                age={patientData?.age}
                gender={patientData?.gender}
              />
            </Grid>
          )}

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              disabled={
                (!formik?.isValid || isLoading) &&
                intent !== vitalsMessages?.intents?.view
              }
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color='inherit' />
                ) : null
              }
            >
              {isLoading
                ? vitalsComponent?.buttons?.saving
                : vitalsComponent?.buttons?.save}
            </Button>
          </Grid>
        </StyledFormGrid>
      </form>
    </StyledPaper>
  )
}

export default CampPatientVitals
